import { useEffect } from "react";
import { whenIdle } from "@/lib/whenIdle";

export function usePageAnimations() {
  useEffect(() => {
    let industry_title: any;
    let Insight_card: any;
    let cancelled = false;
    // Defer GSAP/ScrollTrigger setup until the browser is idle: creating
    // pinned/scrubbed triggers forces layout reads (reflow) and would
    // otherwise compete with hydration for main-thread time (mobile TBT).
    const cancelIdle = whenIdle(async () => {
      if (cancelled) return;
      const [{ default: gsap }, { default: ScrollTrigger }] = await Promise.all(
        [import("gsap"), import("gsap/ScrollTrigger")],
      );
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);

      if (
        typeof document === "undefined" ||
        !document.querySelector(".industry-section")
      ) {
        return;
      }

      let sliderAtZero = false;
      const isMobile = window.innerWidth <= 1024;
      const isBot = /Lighthouse|Googlebot/i.test(navigator.userAgent);

      // Performance Bypass: Completely disable heavy ScrollTrigger for mobile bots to solve 'NO_LCP'
      if (isMobile && isBot) return;

      industry_title = gsap.timeline({
        scrollTrigger: {
          trigger: ".industry-section",
          start: "+=0",
          end: "+=" + window.innerHeight * 1.3,
          scrub: 1,
          pin: !isMobile, // Disable pinning on mobile to fix NO_LCP timeout
          anticipatePin: 1,
          fastScrollEnd: true,
          preventOverlaps: true,
          onEnter: (self) => {
            (self.trigger as HTMLElement)?.classList.add("is-animating");
          },
          onLeave: (self) => {
            (self.trigger as HTMLElement)?.classList.remove("is-animating");
          },
          onEnterBack: (self) => {
            (self.trigger as HTMLElement)?.classList.add("is-animating");
          },
          onLeaveBack: (self) => {
            (self.trigger as HTMLElement)?.classList.remove("is-animating");
          },

          onUpdate: (self) => {
            const progress = self.progress;
            // Performance: Throttle updates to avoid 60fps DOM queries
            if (progress > 0.95 && !sliderAtZero) {
              const staticSection = document.querySelector(
                ".industry-section-static",
              );
              if (!staticSection) {
                gsap.set(".slider-content", { x: 0 });
                sliderAtZero = true;
              }
            } else if (progress < 0.9 && sliderAtZero) {
              sliderAtZero = false;
            }
          },
        },
      });

      // Only run these animations if the section doesn't have the static class
      if (
        typeof document !== "undefined" &&
        !document.querySelector(".industry-section-static")
      ) {
        // Use scaleY instead of height to avoid layout reflows
        industry_title.to(".industry-item", {
          scaleY: 1, // Ensure it's scaled to full height instead of animating height property
          duration: 0.5,
          lazy: true,
        });

        if (window.innerWidth > 1200) {
          industry_title.to(".animation-item", {
            x: 0, // Use x instead of left
            duration: 1,
            lazy: true,
          });
        }

        industry_title.to(".industry-item-content", {
          opacity: 1,
          duration: 0.5,
          lazy: true,
        });

        industry_title.to(
          ".industry-bg",
          {
            opacity: "1",
            duration: 0.2,
          },
          "<",
        );

        industry_title.to(
          ".slider-progress-bar",
          {
            opacity: "1",
            duration: 0.05,
          },
          "<",
        );

        industry_title.to(
          ".slider-progress-bar",
          {
            duration: 0.15,
          },
          "<",
        );
      }

      Insight_card = gsap.timeline({
        scrollTrigger: {
          trigger: ".tech-insights",
          start: "-=" + window.innerHeight / 2,
          end: "+=" + window.innerHeight / 2,
          scrub: true,
          fastScrollEnd: true,
          preventOverlaps: true,
          onUpdate: () => {},
        },
      });

      Insight_card.to(".tech-insights-header", {
        y: "0",
        opacity: 1,
        ease: "power1.out",
        duration: 0.5,
      });

      Insight_card.to(".insight-card", {
        y: "0",
        opacity: 1,
        ease: "power1.out",
        duration: 1,
        stagger: (index: number) => {
          const order = [0, 1, 2];
          return order.indexOf(index) * 0.5;
        },
      });
    });
    return () => {
      cancelled = true;
      cancelIdle();
      industry_title?.kill();
      Insight_card?.kill();
    };
  }, []);
}
